/**
 * WhatsNewModal — Modal "O que mudou" pós-deploy v0.99.11.
 *
 * Explica em 3 slides a reorganização do menu no conceito "Cérebro".
 * Aparece uma única vez por navegador (localStorage) e some. Usa flag
 * `AGENTUS_WHATSNEW_KEY` com versão embutida — bumps futuros ativam
 * um modal novo pra todos automaticamente.
 *
 * Design: fiel ao padrão Apple monocromático do app.
 */
import { useEffect, useState } from "react";
import { Brain, Sparkle, ChatCircleDots, ChartLineUp, CaretRight, CaretLeft, X, Confetti } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const AGENTUS_WHATSNEW_KEY = "agentus_whatsnew_seen_v0_99_11";
// v0.99.45 · Fev/2026 — orquestração de overlays no first-login.
// Precisa coincidir com `OnboardingTour.TOUR_FLAG_KEY` para
// detectarmos se o usuário já concluiu o tour.
const TOUR_FLAG_KEY = "agentus_tour_v11_completed";

const SLIDES = [
  {
    icon: Brain,
    kicker: "O que mudou",
    title: "Agora é Cérebro, não Operação",
    body: "Renomeamos a Central de Operação para Cérebro — porque é exatamente isso: o cérebro do AgentUS. Tudo que a IA sabe (identidade, vozes, serviços, horários, políticas) mora aqui, e só aqui.",
    bullets: [
      "Mesmo conteúdo, nome mais claro",
      "Nova aba: Vozes da IA (o antigo 'Mensagens')",
      "Nova aba: Fontes conectadas — declare se você tem sistema de gestão",
    ],
  },
  {
    icon: Sparkle,
    kicker: "Menu novo",
    title: "5 pilares, direto ao ponto",
    body: "Reorganizamos o menu para refletir o que você realmente faz no AgentUS. Nada foi apagado — só ficou mais fácil de achar.",
    bullets: [
      "Cérebro — o que a IA sabe",
      "Automações — quando a IA age",
      "Conversas — WhatsApp, agenda, perguntas pendentes e sua base de clientes",
      "Impacto — o que a IA rendeu (ex-'ROI por regra')",
      "Configuração — o resto",
    ],
  },
  {
    icon: ChartLineUp,
    kicker: "Impacto do AgentUS",
    title: "Agora com nome que faz sentido",
    body: "O que era 'ROI por regra' virou 'Impacto do AgentUS'. É o mesmo relatório: mostra quanto a IA gerou de receita, agendamentos e comparecimentos — regra por regra.",
    bullets: [
      "Não é o faturamento do seu ERP",
      "É só o que o AgentUS produziu no período",
      "Serve pra você medir o retorno real da IA",
    ],
  },
];

export default function WhatsNewModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    try {
      const seen = localStorage.getItem(AGENTUS_WHATSNEW_KEY);
      if (seen) return;
      // v0.99.45 · Fev/2026 — orquestração: no first-login (quando o
      // OnboardingTour ainda vai rodar), NÃO abrimos o WhatsNewModal.
      // O tour já contempla as mesmas novidades e abrir os dois ao
      // mesmo tempo deixa o dashboard "bagunçado" (modal cobrindo tour,
      // sidebar escondida, cards sem contexto). O WhatsNew é pra
      // usuários QUE JÁ VIRAM O APP e precisam saber o que mudou —
      // não pra quem está entrando pela primeira vez.
      const tourKey = `${TOUR_FLAG_KEY}:${user.id}`;
      const tourDone = localStorage.getItem(tourKey);
      if (!tourDone) return;
      setOpen(true);
    } catch { /* localStorage bloqueado — não abrir modal */ }
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(AGENTUS_WHATSNEW_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  const slide = SLIDES[step];
  const Icon = slide.icon;
  const isLast = step === SLIDES.length - 1;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="whatsnew-modal"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-slate-50 to-slate-100 border-b border-slate-200">
          <button
            type="button"
            onClick={dismiss}
            data-testid="whatsnew-close"
            className="absolute right-3 top-3 p-1.5 rounded-md text-slate-500 hover:bg-slate-200/70"
            aria-label="Fechar"
          >
            <X size={16} weight="bold" />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <Confetti size={14} weight="fill" className="text-slate-900" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-700">
              {slide.kicker}
            </span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Icon size={18} weight="fill" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 tracking-tight leading-snug">
              {slide.title}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-[13.5px] text-slate-700 leading-relaxed">{slide.body}</p>
          <ul className="mt-4 space-y-2">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex gap-2 text-[12.5px] text-slate-600 leading-relaxed">
                <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-slate-400" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          {/* Dots */}
          <div className="flex items-center gap-1.5" data-testid="whatsnew-dots">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === step ? "bg-slate-900" : "bg-slate-300"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                data-testid="whatsnew-prev"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:text-slate-900"
              >
                <CaretLeft size={12} weight="bold" /> Voltar
              </button>
            )}
            <button
              type="button"
              onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}
              data-testid={isLast ? "whatsnew-done" : "whatsnew-next"}
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-md bg-slate-900 text-white text-[12.5px] font-medium hover:bg-slate-800 transition-colors"
            >
              {isLast ? "Entendi" : "Próximo"}
              {!isLast && <CaretRight size={12} weight="bold" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
